import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardMedia,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ImageList,
  ImageListItem,
  Divider,
  Stack,
  Chip,
  Badge,
  CircularProgress,
  Fade,
  Slide,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_URL } from "../config";
import { clearAllCaches, forceHardReload } from "../utils/cacheBuster";
import SEOHead from "../components/SEOHead";

interface ClassDetails {
  _id: string;
  name: string;
  description: string;
  type: "candle" | "plant";
  mainImage: string;
  thumbnailImage?: string;
  gallery: {
    showcase: string[];
    studentWork: string[];
  };
  details: {
    duration: string;
    materials: string[];
    price: number;
    schedule: string;
    location: string;
  };
}

const ClassDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [classData, setClassData] = useState<ClassDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [showcaseFiles, setShowcaseFiles] = useState<File[]>([]);
  const [studentWorkFiles, setStudentWorkFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState<{
    main: boolean;
    showcase: boolean;
    studentWork: boolean;
  }>({ main: false, showcase: false, studentWork: false });
  const { user } = useAuth();
  const fallbackCanonical = `https://cosylabdesigns.com/classes/${id || ""}`;
  const fallbackTitle = classData?.name
    ? `${classData.name} - CosyLab`
    : "課程詳情 - CosyLab";
  const fallbackDescription = classData?.description
    ? classData.description.length > 160
      ? `${classData.description.slice(0, 157)}...`
      : classData.description
    : "查看 CosyLab 課程詳情、作品示例與工作坊安排。課程內容正在更新中。";

  // Image URL validation function
  const isValidImageUrl = (url: string): boolean => {
    return !!(url && (url.startsWith("http") || url.startsWith("data:")));
  };

  // Delete individual images
  const handleImageDelete = (
    index: number,
    type: "showcase" | "studentWork"
  ) => {
    if (!classData) return;

    const updatedImages = [...classData.gallery[type]];
    updatedImages.splice(index, 1);

    setClassData({
      ...classData,
      gallery: {
        ...classData.gallery,
        [type]: updatedImages,
      },
    });
  };

  // Force update function for class details
  const forceUpdateClassDetails = async () => {
    try {
      setLoading(true);
      setClassData(null);

      // Wait a bit to ensure state is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Fetch fresh data
      await fetchClassDetails();
    } catch (err) {
      setError("Failed to refresh class details");
    }
  };

  useEffect(() => {
    fetchClassDetails();

    // Set up SMART auto-refresh for class details
    const intervalId = setInterval(() => {
      // Don't refresh if user is actively editing
      if (editDialogOpen) {
        return;
      }

      fetchClassDetails();
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [id, editDialogOpen]);

  // Auto-refresh when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !editDialogOpen) {
        fetchClassDetails();
      }
    };

    const handleFocus = () => {
      if (!editDialogOpen) {
        fetchClassDetails();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [editDialogOpen]);

  const fetchClassDetails = async () => {
    try {
      // Remove cache-busting parameters to fix SEO issues
      const url = `${API_URL}/api/classes/${id}`;

      const response = await axios.get(url, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        timeout: 15000, // 15 second timeout
      });

      setClassData(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load class details");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !classData) return;

    try {
      setSubmitting(true);
      // Validate required fields
      if (!classData.name || !classData.description || !classData.type) {
        setError("Please fill in all required fields");
        return;
      }

      // Validate details
      if (
        !classData.details.duration ||
        !classData.details.price ||
        !classData.details.schedule ||
        !classData.details.location ||
        !Array.isArray(classData.details.materials)
      ) {
        setError("Please fill in all class details");
        return;
      }

      const formData = new FormData();

      // Add text fields first
      formData.append("name", classData.name);
      formData.append("description", classData.description);
      formData.append("type", classData.type);
      formData.append("details", JSON.stringify(classData.details));

      // Add files if they exist
      if (mainImageFile) {
        formData.append("mainImage", mainImageFile, mainImageFile.name);
      }

      // Add showcase images
      showcaseFiles.forEach((file, index) => {
        formData.append(`showcase`, file, file.name);
      });

      // Add student work images
      studentWorkFiles.forEach((file, index) => {
        formData.append(`studentWork`, file, file.name);
      });

      // Log the request data for debugging

      // Log FormData contents for debugging
      const formDataObj: Record<string, any> = {};
      formData.forEach((value, key) => {
        formDataObj[key] =
          value instanceof File
            ? {
                name: value.name,
                type: value.type,
                size: value.size,
              }
            : value;
      });

      const response = await axios.put(
        `${API_URL}/api/classes/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
          },
        }
      );

      if (response.data) {
        // Clear file states
        setMainImageFile(null);
        setShowcaseFiles([]);
        setStudentWorkFiles([]);

        // Close dialog
        setEditDialogOpen(false);

        // Force complete refresh to ensure fresh data
        await forceUpdateClassDetails();
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorData = err.response?.data;
        if (errorData?.missing) {
          const missingFields = Object.entries(errorData.missing)
            .filter(([_, isMissing]) => isMissing)
            .map(([field]) => field)
            .join(", ");
          setError(`Missing required fields: ${missingFields}`);
        } else {
          setError(errorData?.message || "Failed to update class");
        }
      } else {
        setError("Failed to update class");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <SEOHead
          title={fallbackTitle}
          description={fallbackDescription}
          canonical={fallbackCanonical}
        />
        <Typography variant="h1" sx={{ fontWeight: 700, mb: 2 }}>
          課程詳情
        </Typography>
        <Typography color="text.secondary">
          課程內容正在整理中，頁面資料會在載入完成後更新。
        </Typography>
      </Container>
    );

  if (error)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <SEOHead
          title={fallbackTitle}
          description={fallbackDescription}
          canonical={fallbackCanonical}
        />
        <Typography variant="h1" sx={{ fontWeight: 700, mb: 2 }}>
          課程詳情
        </Typography>
        <Typography color="error">
          目前未能載入這個課程頁面資料，請稍後再試。
        </Typography>
      </Container>
    );

  if (!classData)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <SEOHead
          title={fallbackTitle}
          description={fallbackDescription}
          canonical={fallbackCanonical}
        />
        <Typography variant="h1" sx={{ fontWeight: 700, mb: 2 }}>
          課程詳情
        </Typography>
        <Typography color="text.secondary">
          暫時找不到這個課程資料。
        </Typography>
      </Container>
    );

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: `
        radial-gradient(ellipse at top left, rgba(139, 195, 74, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(104, 159, 56, 0.06) 0%, transparent 50%),
        linear-gradient(180deg, rgba(250, 251, 248, 1) 0%, rgba(245, 248, 242, 1) 100%)
      `,
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(139, 195, 74, 0.02) 35px, rgba(139, 195, 74, 0.02) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(104, 159, 56, 0.02) 35px, rgba(104, 159, 56, 0.02) 70px)
        `,
        pointerEvents: "none",
        zIndex: 0,
      }
    }}>
      <Container maxWidth="lg" sx={{ 
        py: 4,
        position: "relative",
        zIndex: 1,
      }}>
        <SEOHead
          title={fallbackTitle}
          description={fallbackDescription}
          canonical={fallbackCanonical}
        />
        <Slide direction="down" in={true} timeout={800}>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid",
              borderColor: "rgba(139, 195, 74, 0.2)",
              pb: 2,
            }}
          >
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {classData.name}
            </Typography>
        {user && user.isOwner && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                forceUpdateClassDetails();
              }}
              sx={{
                color: "#2D5A27",
                borderColor: "#2D5A27",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "rgba(139, 195, 74, 0.08)",
                  borderColor: "#689F38",
                },
              }}
            >
              刷新資料
            </Button>

            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={async () => {
                await clearAllCaches();
                forceHardReload();
              }}
              sx={{
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "rgba(237, 108, 2, 0.04)",
                },
              }}
            >
              清除緩存
            </Button>

            <IconButton
              onClick={() => setEditDialogOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                color: "white",
                "&:hover": { 
                  background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                },
              }}
            >
              <EditIcon />
            </IconButton>
          </Stack>
        )}
      </Box>
    </Slide>

    <Slide direction="up" in={true} timeout={1000}>
      <Box sx={{ mb: { xs: 3, md: 6 } }}>
        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(139, 195, 74, 0.15)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 16px 50px rgba(139, 195, 74, 0.2)",
            },
          }}
        >
          <CardMedia
            component="img"
            sx={{
              objectFit: "cover",
              width: "100%",
              height: { xs: 200, md: 500 },
            }}
            image={classData.mainImage}
            alt={classData.name}
          />
        </Card>
      </Box>
    </Slide>

    <Fade in={true} timeout={1200}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          mb: 6,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              mb: 3,
              background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Class Description
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{
              lineHeight: 1.8,
              color: "text.secondary",
              whiteSpace: "pre-line",
            }}
          >
            {classData.description}
          </Typography>
        </Box>

        <Card
          sx={{
            p: 3,
            flex: 1,
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(139, 195, 74, 0.08)",
            background: `
              linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 251, 248, 0.95) 100%)
            `,
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(139, 195, 74, 0.1)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px rgba(139, 195, 74, 0.12)",
            },
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              mb: 3,
              background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Class Details
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body1">
                {classData.details.duration}
              </Typography>
            </Box>
            {/* Schedule, Location and Materials intentionally hidden */}
          </Stack>
        </Card>
      </Box>
    </Fade>

    <Fade in={true} timeout={1400}>
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 600, 
            mb: 3,
            background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Class Showcase
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {classData.gallery.showcase.map((image, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(139, 195, 74, 0.12)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "scale(1.03) translateY(-4px)",
                  boxShadow: "0 16px 40px rgba(139, 195, 74, 0.2)",
                },
              }}
            >
              <CardMedia
                component="img"
                image={image}
                alt={`Showcase ${index + 1}`}
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 250, md: 300 },
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </Card>
          ))}
        </Box>
      </Box>
    </Fade>

    <Fade in={true} timeout={1600}>
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 600, 
            mb: 3,
            background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Student Work
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {classData.gallery.studentWork.map((image, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(139, 195, 74, 0.12)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "scale(1.03) translateY(-4px)",
                  boxShadow: "0 16px 40px rgba(139, 195, 74, 0.2)",
                },
              }}
            >
              <CardMedia
                component="img"
                image={image}
                alt={`Student Work ${index + 1}`}
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 250, md: 300 },
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </Card>
          ))}
        </Box>
      </Box>
    </Fade>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          Edit Class
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Name"
                value={classData.name}
                onChange={(e) =>
                  setClassData({ ...classData, name: e.target.value })
                }
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={classData.description}
                onChange={(e) =>
                  setClassData({ ...classData, description: e.target.value })
                }
                multiline
                rows={4}
                required
              />
              <TextField
                fullWidth
                select
                label="Type"
                value={classData.type}
                onChange={(e) =>
                  setClassData({
                    ...classData,
                    type: e.target.value as "candle" | "plant",
                  })
                }
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="candle">Candle Making</option>
                <option value="plant">Plant Care</option>
              </TextField>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Main Image
                  {classData.mainImage &&
                    isValidImageUrl(classData.mainImage) && (
                      <Chip
                        label="Currently Live"
                        size="small"
                        color="success"
                        sx={{ ml: 1 }}
                      />
                    )}
                </Typography>

                {/* Display existing main image */}
                {classData.mainImage &&
                  isValidImageUrl(classData.mainImage) && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        component="img"
                        src={classData.mainImage}
                        alt="Current Main Image"
                        loading="lazy"
                        sx={{
                          width: 120,
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "3px solid",
                          borderColor: "success.main",
                          display: "block",
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        ↑ Currently live main image
                      </Typography>
                    </Box>
                  )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Upload new main image:
                </Typography>
                <input
                  accept="image/*"
                  type="file"
                  onChange={(e) =>
                    setMainImageFile(e.target.files?.[0] || null)
                  }
                  style={{ width: "100%" }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Showcase Images
                  <Chip
                    label={`${
                      classData.gallery.showcase.filter(isValidImageUrl).length
                    } currently live`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Typography>

                {/* Display existing showcase images */}
                {classData.gallery.showcase.filter(isValidImageUrl).length >
                  0 && (
                  <Box sx={{ mb: 2 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {classData.gallery.showcase
                        .filter(isValidImageUrl)
                        .map((image, index) => (
                          <Badge
                            key={index}
                            badgeContent={
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleImageDelete(index, "showcase")
                                }
                                sx={{
                                  bgcolor: "error.main",
                                  color: "white",
                                  width: 20,
                                  height: 20,
                                  "&:hover": { bgcolor: "error.dark" },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            }
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <Box
                              component="img"
                              src={image}
                              alt={`Showcase ${index + 1}`}
                              loading="lazy"
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "2px solid",
                                borderColor: "primary.main",
                              }}
                            />
                          </Badge>
                        ))}
                    </Stack>
                  </Box>
                )}

                <input
                  accept="image/*"
                  type="file"
                  multiple
                  onChange={(e) =>
                    setShowcaseFiles(Array.from(e.target.files || []))
                  }
                  style={{ width: "100%" }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Student Work Images
                  <Chip
                    label={`${
                      classData.gallery.studentWork.filter(isValidImageUrl)
                        .length
                    } currently live`}
                    size="small"
                    color="secondary"
                    sx={{ ml: 1 }}
                  />
                </Typography>

                {/* Display existing student work images */}
                {classData.gallery.studentWork.filter(isValidImageUrl).length >
                  0 && (
                  <Box sx={{ mb: 2 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {classData.gallery.studentWork
                        .filter(isValidImageUrl)
                        .map((image, index) => (
                          <Badge
                            key={index}
                            badgeContent={
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleImageDelete(index, "studentWork")
                                }
                                sx={{
                                  bgcolor: "error.main",
                                  color: "white",
                                  width: 20,
                                  height: 20,
                                  "&:hover": { bgcolor: "error.dark" },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            }
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <Box
                              component="img"
                              src={image}
                              alt={`Student Work ${index + 1}`}
                              loading="lazy"
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "2px solid",
                                borderColor: "secondary.main",
                              }}
                            />
                          </Badge>
                        ))}
                    </Stack>
                  </Box>
                )}

                <input
                  accept="image/*"
                  type="file"
                  multiple
                  onChange={(e) =>
                    setStudentWorkFiles(Array.from(e.target.files || []))
                  }
                  style={{ width: "100%" }}
                />
              </Box>

              <Divider />

              <Typography variant="h6" gutterBottom>
                Class Details
              </Typography>
              <TextField
                fullWidth
                label="Duration"
                value={classData.details.duration}
                onChange={(e) =>
                  setClassData({
                    ...classData,
                    details: {
                      ...classData.details,
                      duration: e.target.value,
                    },
                  })
                }
                required
              />
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={classData.details.price}
                onChange={(e) =>
                  setClassData({
                    ...classData,
                    details: {
                      ...classData.details,
                      price: Number(e.target.value),
                    },
                  })
                }
                required
              />
              <TextField
                fullWidth
                label="Schedule"
                value={classData.details.schedule}
                onChange={(e) =>
                  setClassData({
                    ...classData,
                    details: {
                      ...classData.details,
                      schedule: e.target.value,
                    },
                  })
                }
                required
              />
              <TextField
                fullWidth
                label="Location"
                value={classData.details.location}
                onChange={(e) =>
                  setClassData({
                    ...classData,
                    details: {
                      ...classData.details,
                      location: e.target.value,
                    },
                  })
                }
                required
              />
              <TextField
                fullWidth
                label="Materials (comma-separated)"
                value={classData.details.materials.join(", ")}
                onChange={(e) =>
                  setClassData({
                    ...classData,
                    details: {
                      ...classData.details,
                      materials: e.target.value.split(",").map((m) => m.trim()),
                    },
                  })
                }
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button onClick={() => setEditDialogOpen(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : null}
              sx={{
                px: 3,
                py: 1,
              }}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
    </Box>
  );
};

export default ClassDetail;
